import { forwardRef, ReactNode, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import MuiAutocomplete, { AutocompleteProps as MuiAutocompleteProps } from '@mui/material/Autocomplete';
import { FaIcon } from '@atoms/icons';
import { TextField, TextFieldProps } from './textField';

export type AutocompleteMultipleProps<Value extends string | number> = Omit<
  MuiAutocompleteProps<Option<Value>, true, true, false>,
  'renderInput' | 'value'
> & {
  value?: Value[];
  required?: boolean;
  label?: ReactNode;
  textFieldProps?: TextFieldProps;
  error?: boolean;
  helperText?: ReactNode;
};

export const AutocompleteMultiple = forwardRef(function AutocompleteMultiple<Value extends string | number>(
  { label, options, textFieldProps, error, helperText, value, ...props }: AutocompleteMultipleProps<Value>,
  ref: React.Ref<HTMLInputElement>,
) {
  const { fullWidth, required } = props;
  const internalValue = value?.map((v) => options.find((o) => o.value === v)).filter((v) => v !== undefined);

  const [isOpen, setIsOpen] = useState(false);
  const handleToggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <FormControl fullWidth={fullWidth}>
      <MuiAutocomplete
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        renderInput={(params) => {
          const disabled = params.disabled;
          return (
            <TextField
              {...params}
              label={label}
              required={required}
              error={error}
              helperText={helperText}
              {...textFieldProps}
              inputRef={ref}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{
                      right: '3px',
                      position: 'absolute',
                      top: '50%',
                      transform: 'translate(0, -50%)',
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.preventDefault();
                        handleToggleDropdown();
                      }}
                      disabled={disabled}
                    >
                      <FaIcon name="angle-down" variant="light" opacity={disabled ? 0.5 : 1} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          );
        }}
        value={internalValue}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        options={options}
        disableClearable
        multiple
        selectOnFocus
        handleHomeEndKeys
        limitTags={2}
        getOptionLabel={(option) => option.label}
        ChipProps={{
          color: 'primary',
        }}
        {...props}
      />
    </FormControl>
  );
});

type Option<T extends string | number> = {
  value: T;
  label: string;
  groupBy?: string;
};
